import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEO, { SITE_URL } from '@/components/base/SEO';

export default function PricingGuide() {
  return (
    <main>
      <SEO
        title="Litbuy Spreadsheet Pricing: Plans, Value & Cost Breakdown | LitbuySpreadsheets.online"
        description="Complete pricing analysis covering all Litbuy Spreadsheet plans. Compare features per tier and find the best value for your e-commerce business needs."
        canonicalPath="/litbuy-spreadsheet-pricing"
        keywords="litbuy spreadsheet pricing, litbuy spreadsheet cost, litbuy spreadsheet plans, spreadsheet pricing guide, e-commerce spreadsheet value"
        schemas={[
          {
            '@type': 'Article',
            headline: 'Litbuy Spreadsheet Pricing: Plans and Value Breakdown',
            description: 'Complete pricing analysis covering all Litbuy Spreadsheet plans. Compare features per tier and find the best value for your business.',
            author: { '@type': 'Organization', name: 'Litbuy Editorial Team' },
            publisher: { '@type': 'Organization', name: 'Litbuy Spreadsheets', url: SITE_URL },
            datePublished: '2026-06-15',
            dateModified: '2026-06-20',
            mainEntityOfPage: { '@type': 'WebPage', '@id': `${SITE_URL}/litbuy-spreadsheet-pricing` },
          },
          {
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
              { '@type': 'ListItem', position: 2, name: 'Litbuy Spreadsheet Pricing Guide', item: `${SITE_URL}/litbuy-spreadsheet-pricing` },
            ],
          },
        ]}
      />
      <Navbar />
      <article>
        <section className="relative pt-24 md:pt-28 pb-16 md:pb-20 px-4 md:px-6 bg-background-100">
          <div className="max-w-4xl mx-auto">
            <nav className="flex items-center gap-2 text-xs md:text-sm text-foreground-500 mb-6" aria-label="Breadcrumb">
              <Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer">Home</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-foreground-700 font-medium">Litbuy Spreadsheet Pricing Guide</span>
            </nav>
            <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium mb-4">Pricing</span>
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-900 leading-tight">
              Litbuy Spreadsheet Pricing: Plans and Value Breakdown
            </h1>
            <p className="mt-4 text-sm md:text-base text-foreground-600 leading-relaxed max-w-3xl">
              Complete pricing analysis covering all Litbuy Spreadsheet plans. Compare features per tier and find the best value for your business.
            </p>
          </div>
        </section>

        <section className="relative py-10 md:py-16 px-4 md:px-6 bg-background-50">
          <div className="max-w-4xl mx-auto space-y-14">
            <div>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-5">Understanding Litbuy Spreadsheet Pricing</h2>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">Litbuy Spreadsheet offers flexible pricing designed to scale with your business. Whether you are a solo seller managing 50 products or a multi-store operation with thousands of SKUs, there is a plan that fits your needs without paying for features you will not use.</p>
              <div className="overflow-x-auto rounded-2xl border border-background-200/70 mt-6">
                <table className="w-full text-sm">
                  <thead><tr className="bg-background-100"><th className="text-left px-4 py-3 font-semibold text-foreground-900">Need</th><th className="text-left px-4 py-3 font-semibold text-foreground-700">Best Plan</th><th className="text-left px-4 py-3 font-semibold text-primary-600">Key Feature</th></tr></thead>
                  <tbody className="divide-y divide-background-200/70">
                    <tr><td className="px-4 py-3 text-foreground-700">Small shop</td><td className="px-4 py-3 font-medium text-foreground-800">Starter</td><td className="px-4 py-3 text-primary-600">Up to 500 SKUs</td></tr>
                    <tr className="bg-background-50/50"><td className="px-4 py-3 text-foreground-700">Growing store</td><td className="px-4 py-3 font-medium text-foreground-800">Professional</td><td className="px-4 py-3 text-primary-600">Multi-channel support</td></tr>
                    <tr><td className="px-4 py-3 text-foreground-700">Enterprise</td><td className="px-4 py-3 font-medium text-foreground-800">Business</td><td className="px-4 py-3 text-primary-600">Custom integrations</td></tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="bg-background-100 rounded-2xl p-6 md:p-10 text-center">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-3">Find Your Plan</h2>
              <p className="text-sm text-foreground-600 mb-6 max-w-lg mx-auto">Check current pricing on the official Litbuy store.</p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <a href="https://kaspreadsheet.com/" target="_blank" rel="noopener nofollow" className="whitespace-nowrap cursor-pointer inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-primary-500 text-white font-semibold text-sm hover:bg-primary-600 transition-colors">Visit Main Store <i className="ri-arrow-right-up-line"></i></a>
                <Link to="/" className="whitespace-nowrap cursor-pointer inline-flex items-center gap-2 px-7 py-3.5 rounded-full border border-primary-300 text-primary-600 font-semibold text-sm hover:bg-primary-50 transition-colors">Back to Home <i className="ri-home-4-line"></i></Link>
              </div>
            </div>

            <div className="border-t border-background-200/70 pt-8">
              <h3 className="font-heading text-base font-semibold text-foreground-900 mb-4">Continue Reading</h3>
              <div className="flex flex-wrap gap-2">
                <Link to="/litbuy-spreadsheet-guide" className="whitespace-nowrap cursor-pointer px-4 py-2 rounded-full bg-background-100 text-sm text-foreground-600 hover:text-primary-500 hover:bg-primary-50 transition-colors">Complete Guide</Link>
                <Link to="/litbuy-spreadsheet-pros-cons" className="whitespace-nowrap cursor-pointer px-4 py-2 rounded-full bg-background-100 text-sm text-foreground-600 hover:text-primary-500 hover:bg-primary-50 transition-colors">Pros and Cons</Link>
                <Link to="/" className="whitespace-nowrap cursor-pointer px-4 py-2 rounded-full bg-background-100 text-sm text-foreground-600 hover:text-primary-500 hover:bg-primary-50 transition-colors">Home</Link>
              </div>
            </div>
          </div>
        </section>
      </article>
      <Footer />
    </main>
  );
}